import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useT } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TrackingLink } from "@/components/TrackingLink";
import { toast } from "sonner";

const fmtAt = (s) => (s ? s.slice(0, 16).replace("T", " ") : "—");

// "We need the old set back": Shopify has no API to buy return labels, so the operator
// buys it in Shopify Admin (Canada Post for Canada, USPS for the USA), pastes the link
// here and the CRM e-mails it to the customer.
export function ReturnLabelPanel({ claim }) {
  const qc = useQueryClient();
  const { t } = useT();
  const country = (claim.order?.ship_country || "").toUpperCase();
  const [form, setForm] = useState({ label_url: "", carrier: country === "US" ? "USPS" : "Canada Post", tracking_number: "", send_email: true });
  const attach = useMutation({
    mutationFn: () => api.post(`/claims/${claim.id}/return-label`, form),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["claims"] });
      const l = r.data.return_label || {};
      toast.success(l.emailed_at ? t("Return label e-mailed to the customer") : t("Return label saved"));
      if (l.email_error) toast.error(l.email_error, { duration: 8000 });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const label = claim.return_label;
  return (
    <div className="mt-3 rounded-md border border-sky-200 bg-sky-50 p-3 text-xs space-y-2" data-testid={`claim-return-${claim.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-semibold text-sky-900">↩ {t("Old set to be returned")} · {country === "US" ? "USPS → U.S. address" : "Canada Post → Concord, ON"}</div>
        {claim.shopify_admin_url && (
          <a href={claim.shopify_admin_url} target="_blank" rel="noreferrer" className="underline text-sky-800" data-testid={`claim-shopify-admin-${claim.id}`}>
            {t("Open order in Shopify Admin")} ↗
          </a>
        )}
      </div>
      {label ? (
        <div className="flex flex-wrap items-center gap-3 text-slate-800" data-testid={`claim-return-label-${claim.id}`}>
          <a href={label.url} target="_blank" rel="noreferrer" className="font-semibold underline">🖨 {t("Return label")}</a>
          {label.tracking_number && <TrackingLink number={label.tracking_number} carrier={label.carrier} />}
          <span className="text-slate-500">{t("attached by")} {label.attached_by} · {fmtAt(label.attached_at)}</span>
          <span className={label.emailed_at ? "text-emerald-700 font-semibold" : "text-rose-700 font-semibold"}>
            {label.emailed_at ? `✓ ${t("e-mailed to customer")} ${fmtAt(label.emailed_at)}` : label.email_error ? `✕ ${t("e-mail failed")}: ${label.email_error}` : t("not e-mailed")}
          </span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_160px_auto] gap-2 items-end">
          <div>
            <Label className="text-[11px]">{t("Label link from Shopify Admin (PDF)")}</Label>
            <Input data-testid={`claim-return-url-${claim.id}`} value={form.label_url} onChange={(e) => setForm({ ...form, label_url: e.target.value })} placeholder="https://…" className="h-8 text-xs" />
          </div>
          <div>
            <Label className="text-[11px]">{t("Carrier")}</Label>
            <select data-testid={`claim-return-carrier-${claim.id}`} value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })} className="h-8 w-full rounded-md border border-slate-300 bg-white px-2 text-xs">
              {["Canada Post", "USPS", "UPS", "Purolator", "FedEx", "DHL"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[11px]">{t("Tracking #")}</Label>
            <Input data-testid={`claim-return-tracking-${claim.id}`} value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} className="h-8 text-xs font-mono" />
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 whitespace-nowrap">
              <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} data-testid={`claim-return-email-${claim.id}`} />
              {t("e-mail customer")}
            </label>
            <Button size="sm" className="h-8 bg-sky-700 hover:bg-sky-800 text-white" disabled={attach.isPending || !form.label_url.trim()} onClick={() => attach.mutate()} data-testid={`claim-return-attach-${claim.id}`}>
              {attach.isPending ? "…" : t("Attach label")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
